import { useEffect, useMemo, useState } from 'react';
import {
  AuthConfig,
  AuthType,
  ConnectionConfig,
  CreateDataSourceRequest,
  DataSource,
  DataSourceType,
  DatabaseConnectionConfig,
  DatabaseType,
  FileUploadConnectionConfig,
  GoogleSheetConfig,
  GraphQLConnectionConfig,
  RestProvider,
  RestConnectionConfig,
  TableauConnectionConfig,
  UpdateDataSourceRequest,
} from '@/types/data-source';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateDataSource, useUpdateDataSource, useTestConnection, useDiscoverTableau, useUploadDataSourceFile } from '@/hooks/use-data-sources';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { TemplateSelector } from './TemplateSelector';
import { KeyValueEditor } from './KeyValueEditor';

interface DataSourceFormProps {
  open: boolean;
  onClose: () => void;
  dataSource?: DataSource | null;
}

const dataSourceTypes: { value: DataSourceType; label: string }[] = [
  { value: 'DATABASE', label: 'Database' },
  { value: 'REST', label: 'REST API' },
  { value: 'GRAPHQL', label: 'GraphQL API' },
  { value: 'TABLEAU', label: 'Tableau Public Dashboard' },
  { value: 'FILE_UPLOAD', label: 'File Upload (CSV / XLSX)' },
];

const databaseTypes: { value: DatabaseType; label: string }[] = [
  { value: 'POSTGRES', label: 'PostgreSQL' },
  { value: 'MYSQL', label: 'MySQL / MariaDB' },
  { value: 'MONGODB', label: 'MongoDB' },
  { value: 'SQLSERVER', label: 'SQL Server' },
];

const httpMethods = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const;

const restProviders: { value: RestProvider; label: string }[] = [
  { value: 'GENERIC', label: 'Generic REST API' },
  { value: 'GOOGLE_SHEETS', label: 'Google Sheets (Service Account)' },
];

const defaultPortByDatabase: Record<DatabaseType, number> = {
  POSTGRES: 5432,
  MYSQL: 3306,
  MONGODB: 27017,
  SQLSERVER: 1433,
};

function defaultConnectionForType(type: DataSourceType): ConnectionConfig {
  if (type === 'FILE_UPLOAD') {
    return {
      type: 'FILE_UPLOAD',
      uploadId: '',
    };
  }

  if (type === 'TABLEAU') {
    return {
      type: 'TABLEAU',
      url: '',
      parameters: {},
      filters: [],
      worksheets: [],
      requestDelaySeconds: 1,
    };
  }

  if (type === 'REST') {
    return {
      type: 'REST',
      provider: 'GENERIC',
      baseUrl: '',
      endpoint: '',
      method: 'GET',
    };
  }

  if (type === 'GRAPHQL') {
    return {
      type: 'GRAPHQL',
      endpoint: '',
      query: '',
    };
  }

  return {
    type: 'DATABASE',
    databaseType: 'POSTGRES',
    host: '',
    port: defaultPortByDatabase.POSTGRES,
    database: '',
    schema: '',
    table: '',
    query: '',
    ssl: false,
  };
}

function stringifyGoogleSheetsConfig(sheets: GoogleSheetConfig[] | undefined): string {
  if (!Array.isArray(sheets) || sheets.length === 0) {
    return JSON.stringify(
      [
        {
          name: 'Sheet1',
          range: 'A:Z',
          headerRow: 1,
          dataStartRow: 2,
        },
      ],
      null,
      2
    );
  }

  return JSON.stringify(sheets, null, 2);
}

function parseGoogleSheetsConfig(value: string): GoogleSheetConfig[] {
  const trimmed = value.trim();
  if (!trimmed) return [];

  const parsed = JSON.parse(trimmed);
  if (!Array.isArray(parsed)) {
    throw new Error('Sheet config must be a JSON array');
  }

  return parsed as GoogleSheetConfig[];
}

function defaultAuthForType(type: DataSourceType): AuthConfig {
  if (type === 'DATABASE') {
    return {
      type: 'BASIC',
      credentials: {
        username: '',
        password: '',
      },
    };
  }

  return {
    type: 'NONE',
  };
}

function buildCreateDefaults(type: DataSourceType): CreateDataSourceRequest {
  return {
    name: '',
    description: '',
    type,
    // Uploaded files (BNBA) are assumed to carry personal data by default.
    sensitivity: type === 'FILE_UPLOAD' ? 'PII' : 'NONE',
    connection: defaultConnectionForType(type),
    auth: defaultAuthForType(type),
    metadata: {
      defaultQuery: '',
    },
  };
}

export default function DataSourceForm({ open, onClose, dataSource }: DataSourceFormProps) {
  const isEdit = !!dataSource;
  const createMutation = useCreateDataSource();
  const updateMutation = useUpdateDataSource(dataSource?.id || '');
  const testMutation = useTestConnection();

  const initialType = dataSource?.type ?? 'DATABASE';
  const [formData, setFormData] = useState<CreateDataSourceRequest>(
    buildCreateDefaults(initialType)
  );
  const [updateConnection, setUpdateConnection] = useState(false);
  const [googleSheetsConfig, setGoogleSheetsConfig] = useState(
    stringifyGoogleSheetsConfig(undefined)
  );
  const [googleSheetsConfigError, setGoogleSheetsConfigError] = useState<string | null>(null);
  const [defaultQueryError, setDefaultQueryError] = useState<string | null>(null);
  const [enableOAuth2, setEnableOAuth2] = useState(false);
  const [enableApiToken, setEnableApiToken] = useState(false);
  const uploadMutation = useUploadDataSourceFile();
  const [uploadResult, setUploadResult] = useState<{
    filename: string;
    rowCount: number;
    columns: string[];
  } | null>(null);

  const connection = formData.connection;
  const auth = formData.auth;
  const metadata = formData.metadata || {};

  useEffect(() => {
    if (dataSource) {
      setFormData({
        ...buildCreateDefaults(dataSource.type),
        name: dataSource.name,
        description: dataSource.description || '',
        type: dataSource.type,
        sensitivity: dataSource.sensitivity ?? buildCreateDefaults(dataSource.type).sensitivity,
        metadata: {
          defaultQuery: dataSource.metadata?.defaultQuery || '',
        },
      });
      setUploadResult(null);
      setUpdateConnection(false);
    } else {
      setFormData(buildCreateDefaults('DATABASE'));
      setUploadResult(null);
      setUpdateConnection(false);
    }
  }, [dataSource, open]);

  useEffect(() => {
    if (connection.type !== 'REST' || connection.provider !== 'GOOGLE_SHEETS') {
      setGoogleSheetsConfigError(null);
      return;
    }

    setGoogleSheetsConfig(stringifyGoogleSheetsConfig(connection.sheets));
  }, [connection]);

  const handleTypeChange = (type: DataSourceType) => {
    setFormData((prev) => ({
      ...buildCreateDefaults(type),
      name: prev.name,
      description: prev.description,
    }));
  };

  const handleDatabaseTypeChange = (value: DatabaseType) => {
    if (connection.type !== 'DATABASE') return;
    setFormData((prev) => ({
      ...prev,
      connection: {
        ...connection,
        databaseType: value,
        port: defaultPortByDatabase[value],
      } as DatabaseConnectionConfig,
    }));
  };

  const handleRestProviderChange = (value: RestProvider) => {
    if (connection.type !== 'REST') return;

    const nextConnection: RestConnectionConfig = {
      ...(connection as RestConnectionConfig),
      provider: value,
    };

    if (value === 'GOOGLE_SHEETS') {
      nextConnection.baseUrl = undefined;
      nextConnection.endpoint = undefined;
      nextConnection.method = undefined;
      nextConnection.spreadsheetId = nextConnection.spreadsheetId || '';
      if (Array.isArray(nextConnection.sheets)) {
        nextConnection.sheets = nextConnection.sheets;
      } else {
        try {
          nextConnection.sheets = parseGoogleSheetsConfig(googleSheetsConfig);
        } catch {
          nextConnection.sheets = [];
        }
      }
    } else {
      nextConnection.baseUrl = nextConnection.baseUrl || '';
      nextConnection.endpoint = nextConnection.endpoint || '';
      nextConnection.method = nextConnection.method || 'GET';
      nextConnection.spreadsheetId = undefined;
      nextConnection.sheets = undefined;
    }

    setFormData((prev) => ({
      ...prev,
      connection: nextConnection,
    }));
  };

  const handleAuthTypeChange = (value: AuthType) => {
    let credentials: Record<string, string> | undefined;
    if (value === 'BASIC') {
      credentials = { type: 'BASIC', username: '', password: '' };
    } else if (value === 'API_KEY') {
      credentials = { type: 'API_KEY', key: '', value: '', addTo: 'header' };
    } else if (value === 'BEARER') {
      credentials = { type: 'BEARER', token: '' };
    } else if (value === 'OAUTH') {
      credentials = { type: 'OAUTH', clientId: '', clientSecret: '', tokenUrl: '', scope: '', apiTokenUrl: '', apiTokenPath: '', apiTokenHeader: '', apiTokenUsername: '', apiTokenPassword: '' };
    }

    setFormData((prev) => ({
      ...prev,
      auth: {
        type: value,
        credentials,
      },
    }));
  };

  /**
   * Clean auth credentials before sending to backend.
   * Removes empty string values and fields from disabled flows.
   */
  const cleanAuth = (auth: typeof formData.auth) => {
    if (!auth.credentials) return auth;
    const cleaned = { ...auth.credentials } as Record<string, any>;

    // Strip fields from disabled flows
    if (auth.type === 'OAUTH') {
      if (!enableOAuth2) {
        delete cleaned.clientId;
        delete cleaned.clientSecret;
        delete cleaned.tokenUrl;
        delete cleaned.scope;
      }
      if (!enableApiToken) {
        delete cleaned.apiTokenUrl;
        delete cleaned.apiTokenPath;
        delete cleaned.apiTokenHeader;
        delete cleaned.apiTokenUsername;
        delete cleaned.apiTokenPassword;
      }
    }

    // Remove remaining empty strings
    for (const key of Object.keys(cleaned)) {
      if (cleaned[key] === '') delete cleaned[key];
    }

    cleaned.type = auth.type;
    return { ...auth, credentials: cleaned };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    let submitData = { ...formData };

    // FILE_UPLOAD sources must have a parsed upload before they can be created.
    if (
      !isEdit &&
      submitData.type === 'FILE_UPLOAD' &&
      !(submitData.connection as FileUploadConnectionConfig).uploadId
    ) {
      toast.error('Please upload a CSV or XLSX file first');
      return;
    }

    if (
      submitData.connection.type === 'REST' &&
      submitData.connection.provider === 'GOOGLE_SHEETS'
    ) {
      try {
        const parsedSheets = parseGoogleSheetsConfig(googleSheetsConfig);
        submitData = {
          ...submitData,
          connection: {
            ...submitData.connection,
            sheets: parsedSheets,
          },
        };
        setGoogleSheetsConfigError(null);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Invalid Google Sheets config';
        setGoogleSheetsConfigError(message);
        toast.error(message);
        return;
      }
    }

    try {
      submitData = { ...submitData, auth: cleanAuth(submitData.auth) };

      if (isEdit) {
        const payload: UpdateDataSourceRequest = {
          name: submitData.name,
          description: submitData.description,
          sensitivity: submitData.sensitivity,
          metadata: submitData.metadata,
        };

        if (updateConnection) {
          payload.connection = submitData.connection;
          payload.auth = submitData.auth;
        }

        await updateMutation.mutateAsync(payload);
      } else {
        await createMutation.mutateAsync(submitData);
      }
      onClose();
    } catch (error) {
      // Error handled by mutation
    }
  };

  const handleTestConnection = async () => {
    try {
      await testMutation.mutateAsync({
        connection: formData.connection,
        auth: cleanAuth(formData.auth),
      });
    } catch (error) {
      // Error handled by mutation
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  const isDatabase = useMemo(() => connection.type === 'DATABASE', [connection.type]);
  const isRest = useMemo(() => connection.type === 'REST', [connection.type]);
  const isGraphql = useMemo(() => connection.type === 'GRAPHQL', [connection.type]);
  const isTableau = useMemo(() => connection.type === 'TABLEAU', [connection.type]);
  const isFileUpload = useMemo(() => connection.type === 'FILE_UPLOAD', [connection.type]);

  const handleFileSelected = async (file: File | undefined) => {
    if (!file) return;
    try {
      const result = await uploadMutation.mutateAsync(file);
      setUploadResult({
        filename: result.filename,
        rowCount: result.rowCount,
        columns: result.columns,
      });
      setFormData((prev) => ({
        ...prev,
        connection: {
          type: 'FILE_UPLOAD',
          uploadId: result.uploadId,
          filename: result.filename,
          format: result.format,
        },
      }));
      toast.success(`Parsed ${result.rowCount} rows, ${result.columns.length} columns`);
    } catch {
      // toast handled in hook
    }
  };
  const discoverTableau = useDiscoverTableau();
  const [tableauWorksheets, setTableauWorksheets] = useState<
    Array<{ name: string; columns: string[]; rowCount: number }>
  >([]);
  const [tableauParametersMeta, setTableauParametersMeta] = useState<
    Array<{ name: string; values: string[] }>
  >([]);

  const handleTableauDiscover = async () => {
    if (connection.type !== 'TABLEAU' || !connection.url) {
      toast.error('Enter the Tableau view URL first');
      return;
    }
    try {
      const result = await discoverTableau.mutateAsync({
        url: connection.url,
        parameters: connection.parameters || {},
        filters: connection.filters || [],
        requestDelaySeconds: connection.requestDelaySeconds,
      });
      setTableauWorksheets(result.worksheets || []);
      setTableauParametersMeta(result.parameters || []);
      toast.success(`Found ${result.worksheets?.length ?? 0} worksheet(s)`);
    } catch {
      // toast handled in hook
    }
  };

  const toggleTableauWorksheet = (name: string) => {
    if (connection.type !== 'TABLEAU') return;
    const current = connection.worksheets || [];
    const exists = current.some((w) => w.name === name);
    const next = exists
      ? current.filter((w) => w.name !== name)
      : [...current, { name }];
    setFormData((prev) => ({
      ...prev,
      connection: { ...(connection as TableauConnectionConfig), worksheets: next },
    }));
  };
  const isGoogleSheets = useMemo(
    () => isRest && (connection as RestConnectionConfig).provider === 'GOOGLE_SHEETS',
    [connection, isRest]
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Data Source' : 'Add Data Source'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Update your data source configuration'
              : 'Configure a new data source connection'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Sales Warehouse"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">Type *</Label>
              <Select
                value={formData.type}
                onValueChange={handleTypeChange}
                disabled={isEdit}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {dataSourceTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isEdit && (
                <p className="text-xs text-muted-foreground">
                  Data source type cannot be changed after creation.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  id="sensitivity-pii"
                  type="checkbox"
                  checked={formData.sensitivity === 'PII'}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      sensitivity: e.target.checked ? 'PII' : 'NONE',
                    })
                  }
                  className="h-4 w-4 rounded border-border"
                />
                <label htmlFor="sensitivity-pii" className="cursor-pointer text-sm font-medium">
                  Contains personal data (PII)
                </label>
                {formData.sensitivity === 'PII' && (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                    PII
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                When enabled, extracted rows are encrypted at rest and purged after
                transformation, this source cannot be published directly (only gated,
                column-contracted derivatives), and its rows are never sent to the AI
                assistant.
              </p>
            </div>
          </div>

          {isEdit && (
            <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 text-sm">
              <input
                id="update-connection"
                type="checkbox"
                checked={updateConnection}
                onChange={(e) => setUpdateConnection(e.target.checked)}
                className="h-4 w-4 rounded border-border"
              />
              <label htmlFor="update-connection" className="cursor-pointer text-sm">
                Update connection & authentication settings
              </label>
            </div>
          )}

          {(isEdit ? updateConnection : true) && (
            <>
              <div className="space-y-4 pt-2 border-t">
                <h3 className="text-sm font-medium">Connection Details</h3>

                {isDatabase && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="databaseType">Database Type *</Label>
                      <Select
                        value={(connection as DatabaseConnectionConfig).databaseType}
                        onValueChange={(value) =>
                          handleDatabaseTypeChange(value as DatabaseType)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select database" />
                        </SelectTrigger>
                        <SelectContent>
                          {databaseTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="host">Host *</Label>
                        <Input
                          id="host"
                          value={(connection as DatabaseConnectionConfig).host}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              connection: {
                                ...(connection as DatabaseConnectionConfig),
                                host: e.target.value,
                              },
                            })
                          }
                          placeholder="db.example.com"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="port">Port *</Label>
                        <Input
                          id="port"
                          type="number"
                          value={(connection as DatabaseConnectionConfig).port}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              connection: {
                                ...(connection as DatabaseConnectionConfig),
                                port: Number(e.target.value),
                              },
                            })
                          }
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="database">Database *</Label>
                      <Input
                        id="database"
                        value={(connection as DatabaseConnectionConfig).database}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            connection: {
                              ...(connection as DatabaseConnectionConfig),
                              database: e.target.value,
                            },
                          })
                        }
                        placeholder="warehouse"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="schema">Schema</Label>
                        <Input
                          id="schema"
                          value={(connection as DatabaseConnectionConfig).schema || ''}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              connection: {
                                ...(connection as DatabaseConnectionConfig),
                                schema: e.target.value,
                              },
                            })
                          }
                          placeholder="public"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="table">Table</Label>
                        <Input
                          id="table"
                          value={(connection as DatabaseConnectionConfig).table || ''}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              connection: {
                                ...(connection as DatabaseConnectionConfig),
                                table: e.target.value,
                              },
                            })
                          }
                          placeholder="orders"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="query">Query (optional)</Label>
                      <Textarea
                        id="query"
                        value={(connection as DatabaseConnectionConfig).query || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            connection: {
                              ...(connection as DatabaseConnectionConfig),
                              query: e.target.value,
                            },
                          })
                        }
                        placeholder="SELECT * FROM orders WHERE created_at >= CURRENT_DATE - INTERVAL '7 days'"
                        rows={3}
                      />
                    </div>

                    <div className="flex items-center gap-2 text-sm">
                      <input
                        id="ssl"
                        type="checkbox"
                        checked={(connection as DatabaseConnectionConfig).ssl || false}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            connection: {
                              ...(connection as DatabaseConnectionConfig),
                              ssl: e.target.checked,
                            },
                          })
                        }
                        className="h-4 w-4 rounded border-border"
                      />
                      <label htmlFor="ssl">Use SSL</label>
                    </div>
                  </div>
                )}

                {isRest && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="rest-provider">REST Provider *</Label>
                      <Select
                        value={(connection as RestConnectionConfig).provider || 'GENERIC'}
                        onValueChange={(value) => handleRestProviderChange(value as RestProvider)}
                      >
                        <SelectTrigger id="rest-provider">
                          <SelectValue placeholder="Select provider" />
                        </SelectTrigger>
                        <SelectContent>
                          {restProviders.map((provider) => (
                            <SelectItem key={provider.value} value={provider.value}>
                              {provider.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {isGoogleSheets ? (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="spreadsheet-id">Spreadsheet ID *</Label>
                          <Input
                            id="spreadsheet-id"
                            value={(connection as RestConnectionConfig).spreadsheetId || ''}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                connection: {
                                  ...(connection as RestConnectionConfig),
                                  provider: 'GOOGLE_SHEETS',
                                  spreadsheetId: e.target.value,
                                },
                              })
                            }
                            placeholder="1AbCdEfGhIjKlMnOpQrStUvWxYz"
                            required
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="google-sheets-config">Sheets Config (JSON)</Label>
                          <Textarea
                            id="google-sheets-config"
                            value={googleSheetsConfig}
                            onChange={(e) => setGoogleSheetsConfig(e.target.value)}
                            onBlur={(e) => {
                              try {
                                const parsed = parseGoogleSheetsConfig(e.target.value);
                                setGoogleSheetsConfigError(null);
                                setFormData({
                                  ...formData,
                                  connection: {
                                    ...(connection as RestConnectionConfig),
                                    provider: 'GOOGLE_SHEETS',
                                    sheets: parsed,
                                  },
                                });
                              } catch (error) {
                                setGoogleSheetsConfigError(
                                  error instanceof Error
                                    ? error.message
                                    : 'Invalid sheets JSON config'
                                );
                              }
                            }}
                            rows={8}
                            placeholder='[{"name":"Sheet1","range":"A:Z","headerRow":1,"dataStartRow":2}]'
                          />
                          <p className="text-xs text-muted-foreground">
                            Use one object per sheet. Add optional columnMap for header aliases.
                          </p>
                          {googleSheetsConfigError && (
                            <p className="text-xs text-destructive">{googleSheetsConfigError}</p>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                    <div className="space-y-2">
                      <Label htmlFor="baseUrl">Base URL *</Label>
                      <Input
                        id="baseUrl"
                        value={(connection as RestConnectionConfig).baseUrl || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            connection: {
                              ...(connection as RestConnectionConfig),
                              baseUrl: e.target.value,
                            },
                          })
                        }
                        placeholder="https://api.example.com"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="endpoint">Endpoint *</Label>
                        <Input
                          id="endpoint"
                          value={(connection as RestConnectionConfig).endpoint || ''}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              connection: {
                                ...(connection as RestConnectionConfig),
                                endpoint: e.target.value,
                              },
                            })
                          }
                          placeholder="/v1/orders"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="method">Method *</Label>
                        <Select
                          value={(connection as RestConnectionConfig).method || 'GET'}
                          onValueChange={(value) =>
                            setFormData({
                              ...formData,
                              connection: {
                                ...(connection as RestConnectionConfig),
                                method: value as RestConnectionConfig['method'],
                              },
                            })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select method" />
                          </SelectTrigger>
                          <SelectContent>
                            {httpMethods.map((method) => (
                              <SelectItem key={method} value={method}>
                                {method}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <KeyValueEditor
                      label="Custom Headers"
                      value={(connection as RestConnectionConfig).headers}
                      onChange={(headers) =>
                        setFormData({
                          ...formData,
                          connection: {
                            ...(connection as RestConnectionConfig),
                            headers,
                          },
                        })
                      }
                      keyPlaceholder="Header name (e.g. X-API-TOKEN)"
                      valuePlaceholder="Header value"
                      helpText="Sent with every request. Merged on top of Authorization headers from the Auth section below."
                    />

                    <KeyValueEditor
                      label="Default Query Parameters"
                      value={(connection as RestConnectionConfig).queryParams}
                      onChange={(queryParams) =>
                        setFormData({
                          ...formData,
                          connection: {
                            ...(connection as RestConnectionConfig),
                            queryParams,
                          },
                        })
                      }
                      keyPlaceholder="Param name"
                      valuePlaceholder="Param value"
                      helpText="Appended to the endpoint URL on every request. Per-query pagination params are added on top."
                    />
                      </>
                    )}
                  </div>
                )}

                {isTableau && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="tableau-url">Tableau View URL *</Label>
                      <Input
                        id="tableau-url"
                        value={(connection as TableauConnectionConfig).url || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            connection: {
                              ...(connection as TableauConnectionConfig),
                              url: e.target.value,
                            },
                          })
                        }
                        placeholder="https://dashboard.kemkes.go.id/views/Workbook/View"
                        required
                      />
                      <p className="text-xs text-muted-foreground">
                        Public Tableau view URL. Auth not required for public dashboards.
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleTableauDiscover}
                        disabled={discoverTableau.isPending || !(connection as TableauConnectionConfig).url}
                      >
                        {discoverTableau.isPending ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Loading...
                          </>
                        ) : (
                          'Test & Discover'
                        )}
                      </Button>
                      <span className="text-xs text-muted-foreground">
                        Loads the workbook and lists worksheets / parameters.
                      </span>
                    </div>

                    {tableauWorksheets.length > 0 && (
                      <div className="space-y-2">
                        <Label>Worksheets to Extract</Label>
                        <div className="space-y-1 rounded-md border p-3 max-h-56 overflow-y-auto">
                          {tableauWorksheets.map((ws) => {
                            const selected = ((connection as TableauConnectionConfig).worksheets || []).some(
                              (w) => w.name === ws.name
                            );
                            return (
                              <label key={ws.name} className="flex items-start gap-2 text-sm cursor-pointer py-1">
                                <input
                                  type="checkbox"
                                  className="mt-1 h-4 w-4"
                                  checked={selected}
                                  onChange={() => toggleTableauWorksheet(ws.name)}
                                />
                                <div className="flex-1">
                                  <div className="font-medium">{ws.name}</div>
                                  <div className="text-xs text-muted-foreground">
                                    {ws.rowCount} rows · {ws.columns.length} cols
                                    {ws.columns.length > 0 && (
                                      <> · {ws.columns.slice(0, 4).join(', ')}{ws.columns.length > 4 ? '…' : ''}</>
                                    )}
                                  </div>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Leave all unchecked to extract every worksheet.
                        </p>
                      </div>
                    )}

                    {tableauParametersMeta.length > 0 && (
                      <div className="space-y-2">
                        <Label>Detected Parameters</Label>
                        <div className="rounded-md border bg-muted/30 p-3 text-xs space-y-1 max-h-40 overflow-y-auto">
                          {tableauParametersMeta.map((p) => (
                            <div key={p.name}>
                              <span className="font-mono">{p.name}</span>
                              {p.values.length > 0 && (
                                <span className="text-muted-foreground">
                                  {' '}
                                  · sample: {p.values.slice(0, 3).join(', ')}
                                  {p.values.length > 3 ? '…' : ''}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Use the JSON Parameters field below to override values.
                        </p>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="tableau-params">Parameters Override (JSON)</Label>
                      <Textarea
                        id="tableau-params"
                        value={JSON.stringify(
                          (connection as TableauConnectionConfig).parameters || {},
                          null,
                          2
                        )}
                        onChange={(e) => {
                          try {
                            const parsed = JSON.parse(e.target.value || '{}');
                            setFormData({
                              ...formData,
                              connection: {
                                ...(connection as TableauConnectionConfig),
                                parameters: parsed,
                              },
                            });
                          } catch {
                            // ignore invalid JSON while typing
                          }
                        }}
                        rows={4}
                        placeholder='{"Provinsi":"KALIMANTAN UTARA"}'
                        className="font-mono text-xs"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="tableau-delay">Request Delay (seconds)</Label>
                        <Input
                          id="tableau-delay"
                          type="number"
                          step="0.5"
                          min="0"
                          max="30"
                          value={(connection as TableauConnectionConfig).requestDelaySeconds ?? 1}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              connection: {
                                ...(connection as TableauConnectionConfig),
                                requestDelaySeconds: Number(e.target.value),
                              },
                            })
                          }
                        />
                        <p className="text-xs text-muted-foreground">
                          Polite pause between Tableau requests.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {isGraphql && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="graphql-endpoint">Endpoint *</Label>
                      <Input
                        id="graphql-endpoint"
                        value={(connection as GraphQLConnectionConfig).endpoint}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            connection: {
                              ...(connection as GraphQLConnectionConfig),
                              endpoint: e.target.value,
                            },
                          })
                        }
                        placeholder="https://api.example.com/graphql"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="graphql-query">Query *</Label>
                      <Textarea
                        id="graphql-query"
                        value={(connection as GraphQLConnectionConfig).query}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            connection: {
                              ...(connection as GraphQLConnectionConfig),
                              query: e.target.value,
                            },
                          })
                        }
                        placeholder="query GetOrders { orders { id total } }"
                        rows={4}
                        required
                      />
                    </div>
                  </div>
                )}

                {isFileUpload && (
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="upload-file">File (CSV or XLSX) *</Label>
                      <Input
                        id="upload-file"
                        type="file"
                        accept=".csv,.xlsx,.xlsm"
                        disabled={isEdit || uploadMutation.isPending}
                        onChange={(e) => handleFileSelected(e.target.files?.[0])}
                      />
                      <p className="text-xs text-muted-foreground">
                        The file is parsed on the server and stored encrypted — the raw
                        file is never written to disk. Only .csv and .xlsx are accepted.
                      </p>
                    </div>

                    {uploadMutation.isPending && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Uploading and parsing…
                      </div>
                    )}

                    {isEdit && (
                      <p className="text-xs text-muted-foreground">
                        The uploaded file cannot be replaced here. Create a new data
                        source to upload a different file.
                      </p>
                    )}

                    {uploadResult && (
                      <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
                        <div className="font-medium">{uploadResult.filename}</div>
                        <div className="text-muted-foreground">
                          {uploadResult.rowCount.toLocaleString()} rows ·{' '}
                          {uploadResult.columns.length} columns
                        </div>
                        <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                          {uploadResult.columns.join(', ')}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {!isTableau && !isFileUpload && (
              <div className="space-y-4 pt-4 border-t">
                <h3 className="text-sm font-medium">Authentication</h3>

                <div className="space-y-2">
                  <Label htmlFor="authType">Auth Type *</Label>
                  <Select value={auth.type} onValueChange={(value) => handleAuthTypeChange(value as AuthType)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select auth type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NONE">None</SelectItem>
                      <SelectItem value="BASIC">Basic</SelectItem>
                      <SelectItem value="API_KEY">API Key</SelectItem>
                      <SelectItem value="BEARER">Bearer Token</SelectItem>
                      <SelectItem value="OAUTH">OAuth2 / Token Login</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {auth.type === 'BASIC' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="username">Username *</Label>
                      <Input
                        id="username"
                        value={auth.credentials?.username || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            auth: {
                              ...auth,
                              credentials: {
                                ...(auth.credentials || {}),
                                type: 'BASIC',
                                username: e.target.value,
                              },
                            },
                          })
                        }
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password">Password *</Label>
                      <Input
                        id="password"
                        type="password"
                        value={auth.credentials?.password || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            auth: {
                              ...auth,
                              credentials: {
                                ...(auth.credentials || {}),
                                type: 'BASIC',
                                password: e.target.value,
                              },
                            },
                          })
                        }
                        required={!isEdit || updateConnection}
                      />
                    </div>
                  </div>
                )}

                {auth.type === 'API_KEY' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="apiKey">Header / Param Name *</Label>
                        <Input
                          id="apiKey"
                          value={auth.credentials?.key || ''}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              auth: {
                                ...auth,
                                credentials: {
                                  ...(auth.credentials || {}),
                                  type: 'API_KEY',
                                  key: e.target.value,
                                },
                              },
                            })
                          }
                          placeholder="X-API-Key"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="apiValue">Value *</Label>
                        <Input
                          id="apiValue"
                          value={auth.credentials?.value || ''}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              auth: {
                                ...auth,
                                credentials: {
                                  ...(auth.credentials || {}),
                                  type: 'API_KEY',
                                  value: e.target.value,
                                },
                              },
                            })
                          }
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="addTo">Send API Key In *</Label>
                      <Select
                        value={auth.credentials?.addTo || 'header'}
                        onValueChange={(value) =>
                          setFormData({
                            ...formData,
                            auth: {
                              ...auth,
                              credentials: {
                                ...(auth.credentials || {}),
                                type: 'API_KEY',
                                addTo: value,
                              },
                            },
                          })
                        }
                      >
                        <SelectTrigger id="addTo">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="header">Header</SelectItem>
                          <SelectItem value="query">Query Parameter</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Header: sent as a request header. Query: appended as a URL parameter.
                      </p>
                    </div>
                  </div>
                )}

                {auth.type === 'BEARER' && (
                  <div className="space-y-2">
                    <Label htmlFor="token">Bearer Token *</Label>
                    <Input
                      id="token"
                      value={auth.credentials?.token || ''}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          auth: {
                            ...auth,
                            credentials: {
                              ...(auth.credentials || {}),
                              type: 'BEARER',
                              token: e.target.value,
                            },
                          },
                        })
                      }
                      required
                    />
                  </div>
                )}

                {auth.type === 'OAUTH' && (
                  <div className="space-y-4">
                    {/* Flow 1: OAuth2 Client Credentials */}
                    <div className={`space-y-3 rounded-md border p-3 ${enableOAuth2 ? 'bg-background' : 'bg-muted/30'}`}>
                      <div className="flex items-center gap-2">
                        <input
                          id="enable-oauth2"
                          type="checkbox"
                          checked={enableOAuth2}
                          onChange={(e) => setEnableOAuth2(e.target.checked)}
                          className="h-4 w-4 rounded border-border"
                        />
                        <label htmlFor="enable-oauth2" className="text-xs font-medium cursor-pointer">
                          Step 1: OAuth2 Client Credentials
                        </label>
                      </div>
                      {enableOAuth2 && (
                        <div className="space-y-3 pt-1">
                          <div className="space-y-2">
                            <Label htmlFor="tokenUrl">Token URL *</Label>
                            <Input
                              id="tokenUrl"
                              value={auth.credentials?.tokenUrl || ''}
                              onChange={(e) =>
                                setFormData({ ...formData, auth: { ...auth, credentials: { ...(auth.credentials || {}), type: 'OAUTH', tokenUrl: e.target.value } } })
                              }
                              placeholder="https://splp.layanan.go.id/oauth2/token"
                              required
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="clientId">Client ID *</Label>
                              <Input
                                id="clientId"
                                value={auth.credentials?.clientId || ''}
                                onChange={(e) =>
                                  setFormData({ ...formData, auth: { ...auth, credentials: { ...(auth.credentials || {}), type: 'OAUTH', clientId: e.target.value } } })
                                }
                                required
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="clientSecret">Client Secret *</Label>
                              <Input
                                id="clientSecret"
                                type="password"
                                value={auth.credentials?.clientSecret || ''}
                                onChange={(e) =>
                                  setFormData({ ...formData, auth: { ...auth, credentials: { ...(auth.credentials || {}), type: 'OAUTH', clientSecret: e.target.value } } })
                                }
                                required
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="scope">Scope</Label>
                            <Input
                              id="scope"
                              value={auth.credentials?.scope || ''}
                              onChange={(e) =>
                                setFormData({ ...formData, auth: { ...auth, credentials: { ...(auth.credentials || {}), type: 'OAUTH', scope: e.target.value } } })
                              }
                              placeholder="default"
                            />
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Bearer token fetched via OAuth2 client_credentials grant.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Flow 2: API Token Login */}
                    <div className={`space-y-3 rounded-md border p-3 ${enableApiToken ? 'bg-background' : 'bg-muted/30'}`}>
                      <div className="flex items-center gap-2">
                        <input
                          id="enable-api-token"
                          type="checkbox"
                          checked={enableApiToken}
                          onChange={(e) => setEnableApiToken(e.target.checked)}
                          className="h-4 w-4 rounded border-border"
                        />
                        <label htmlFor="enable-api-token" className="text-xs font-medium cursor-pointer">
                          {enableOAuth2 ? 'Step 2: API Token (secondary)' : 'Token Login'}
                        </label>
                      </div>
                      {enableApiToken && (
                        <div className="space-y-3 pt-1">
                          <div className="space-y-2">
                            <Label htmlFor="apiTokenUrl">Login / Token URL *</Label>
                            <Input
                              id="apiTokenUrl"
                              value={auth.credentials?.apiTokenUrl || ''}
                              onChange={(e) =>
                                setFormData({ ...formData, auth: { ...auth, credentials: { ...(auth.credentials || {}), type: 'OAUTH', apiTokenUrl: e.target.value } } })
                              }
                              placeholder="https://api-splp.layanan.go.id/sdpdn/1.0/login"
                              required
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="apiTokenPath">Response Token Path *</Label>
                              <Input
                                id="apiTokenPath"
                                value={auth.credentials?.apiTokenPath || ''}
                                onChange={(e) =>
                                  setFormData({ ...formData, auth: { ...auth, credentials: { ...(auth.credentials || {}), type: 'OAUTH', apiTokenPath: e.target.value } } })
                                }
                                placeholder="data.token"
                                required
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="apiTokenHeader">Send As Header *</Label>
                              <Input
                                id="apiTokenHeader"
                                value={auth.credentials?.apiTokenHeader || ''}
                                onChange={(e) =>
                                  setFormData({ ...formData, auth: { ...auth, credentials: { ...(auth.credentials || {}), type: 'OAUTH', apiTokenHeader: e.target.value } } })
                                }
                                placeholder="token"
                                required
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="apiTokenUsername">Username</Label>
                              <Input
                                id="apiTokenUsername"
                                value={auth.credentials?.apiTokenUsername || ''}
                                onChange={(e) =>
                                  setFormData({ ...formData, auth: { ...auth, credentials: { ...(auth.credentials || {}), type: 'OAUTH', apiTokenUsername: e.target.value } } })
                                }
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="apiTokenPassword">Password</Label>
                              <Input
                                id="apiTokenPassword"
                                type="password"
                                value={auth.credentials?.apiTokenPassword || ''}
                                onChange={(e) =>
                                  setFormData({ ...formData, auth: { ...auth, credentials: { ...(auth.credentials || {}), type: 'OAUTH', apiTokenPassword: e.target.value } } })
                                }
                              />
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {enableOAuth2
                              ? 'Called with the OAuth2 Bearer token. Response token sent as the specified header.'
                              : 'POST with username/password to get a token. Sent as the specified header on data requests.'}
                          </p>
                        </div>
                      )}
                    </div>

                    {!enableOAuth2 && !enableApiToken && (
                      <p className="text-xs text-destructive">
                        Enable at least one authentication flow above.
                      </p>
                    )}
                  </div>
                )}
              </div>
              )}
            </>
          )}

          {!isFileUpload && (
          <div className="space-y-4 pt-4 border-t">
            {/* Template Selector */}
            {formData.type === 'REST' && (
              <TemplateSelector
                currentQuery={metadata.defaultQuery}
                onTemplateApplied={(query) => {
                  setFormData({
                    ...formData,
                    metadata: {
                      ...metadata,
                      defaultQuery: JSON.stringify(query, null, 2),
                    },
                  });
                }}
              />
            )}

            <div className="space-y-2">
              <Label htmlFor="defaultQuery">Default Extraction Query</Label>
              <Textarea
                id="defaultQuery"
                value={(metadata.defaultQuery as string) || ''}
                onChange={(e) => {
                  if (defaultQueryError) setDefaultQueryError(null);
                  setFormData({
                    ...formData,
                    metadata: {
                      ...metadata,
                      defaultQuery: e.target.value,
                    },
                  });
                }}
                onBlur={(e) => {
                  const raw = e.target.value.trim();
                  if (!raw) {
                    setDefaultQueryError(null);
                    return;
                  }
                  try {
                    JSON.parse(raw);
                    setDefaultQueryError(null);
                  } catch (err) {
                    setDefaultQueryError(
                      err instanceof Error ? err.message : 'Invalid JSON'
                    );
                  }
                }}
                placeholder='Used when triggering manual extraction. Example: {"endpoint":"/api/data","pagination":{"pageParam":"page","pageSize":50,"dataPath":"data.data","totalPagesField":"data.last_page"}}'
                rows={6}
                className={`font-mono text-xs ${
                  defaultQueryError ? 'border-destructive focus-visible:ring-destructive' : ''
                }`}
              />
              {defaultQueryError && (
                <p className="text-xs text-destructive">
                  Invalid JSON — {defaultQueryError}
                </p>
              )}
            </div>
          </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            {!isFileUpload && (
            <Button
              type="button"
              variant="outline"
              onClick={handleTestConnection}
              disabled={isLoading || testMutation.isPending || (isEdit && !updateConnection)}
            >
              {testMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Testing...
                </>
              ) : (
                'Test Connection'
              )}
            </Button>
            )}
            <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {isEdit ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>{isEdit ? 'Update' : 'Create'}</>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
